import { ChecklistMetricStatus } from '../../handoff-checklist-v2/handoff-checklist-v2.types';
import { WieMetricDefinition, WieMetricEvalContext } from '../wie-metrics.types';
import { InsuranceVerificationAdapterService } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { authorizedFetch } from 'helpers/utils';
import { getParsedErrorMessage } from 'redux/axios';
import { buildPayerListNode, PayerListData } from './wie-payer-message';

const TOP_PAYERS_THRESHOLD = 3;

// fully connected = connected AND (if mfa automation supported then mfa must be active)
const isFullyConnected = (payer: {
  connected?: boolean;
  isMfaAutomationSupported?: boolean;
  isAutomatedMfaActive?: boolean;
}) =>
  !!payer.connected &&
  (payer.isMfaAutomationSupported ? !!payer.isAutomatedMfaActive : true);

const connectionScore = (payer: {
  connected?: boolean;
  isMfaAutomationSupported?: boolean;
  isAutomatedMfaActive?: boolean;
}) => (isFullyConnected(payer) ? 2 : payer.connected ? 1 : 0);

export const wiePayerConnectionsActiveMetric: WieMetricDefinition = {
  id: 'wie_payer_connections_active',
  name: 'Web-supported Payer Connections Active',
  isRequired: true,
  allowExceptions: true,
  criteria: [
    {
      description: 'Recommended web-supported payers are connected',
      exceptionType: 'free_text',
      evaluate: async (ctx: WieMetricEvalContext) => {
        try {
          const response = await InsuranceVerificationAdapterService.GetPayerMixAnalysis(
            authorizedFetch(),
            { locationId: ctx.locationId }
          );

          const webSupportedPayers = (response?.payerMixDetails ?? []).filter(
            (payer) => payer.webSupported
          );
          const topPayers = [...webSupportedPayers]
            .sort((a, b) => {
              const countDiff = (b.appointmentCount ?? 0) - (a.appointmentCount ?? 0);
              if (countDiff !== 0) return countDiff;
              return connectionScore(b) - connectionScore(a);
            })
            .slice(0, TOP_PAYERS_THRESHOLD);

          const fullyConnectedPayers = topPayers.filter(isFullyConnected);

          if (topPayers.length > 0 && fullyConnectedPayers.length === topPayers.length) {
            const payerData: PayerListData = {
              type: 'payer-list',
              title: `${fullyConnectedPayers.length}/${topPayers.length} recommended web-supported payers are connected:`,
              payers: topPayers.map((p) => ({
                name: p.payerName || '',
                connected: !!p.connected,
                mfaRequired: !!p.isMfaAutomationSupported,
                mfaActive: !!p.isAutomatedMfaActive,
              })),
            };
            return {
              status: ChecklistMetricStatus.PASSED,
              message: JSON.stringify(payerData),
              messageNode: buildPayerListNode(payerData),
            };
          }

          const n = fullyConnectedPayers.length;
          const total = topPayers.length;

          const payerData: PayerListData = {
            type: 'payer-list',
            title: `Only ${n}/${total} recommended web-supported payers are connected:`,
            payers: topPayers.map((p) => ({
              name: p.payerName || '',
              connected: !!p.connected,
              mfaRequired: !!p.isMfaAutomationSupported,
              mfaActive: !!p.isAutomatedMfaActive,
            })),
          };

          return {
            status: ChecklistMetricStatus.BLOCKED,
            message: JSON.stringify(payerData),
            messageNode: buildPayerListNode(payerData),
          };
        } catch (err) {
          return {
            status: ChecklistMetricStatus.BLOCKED,
            message:
              getParsedErrorMessage(err) ||
              'Failed to verify web-supported payer connections.',
          };
        }
      },
    },
  ],
};
